import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Divider, Empty, Modal, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  AppDispatch,
  exportReport,
  exportReportClear,
  exportReportRequest,
  getReportOptions,
} from "@src/redux";
import * as TYPES from "@src/redux/types";
import {
  DEFAULT_REPORT_SUBJECT_FIELD_KEY_LIST,
  SHORT_QUESTION_MAX_LENGTH,
  resolveShortQuestion,
} from "@src/helpers/report";

interface ReportExportModalProps {
  isModalVisible: boolean;
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  filter: TYPES.ReportFilterInput;
}

/**
 * Turn the base64 workbook the mutation returns into a browser download.
 * Kept local to the modal because this is the only place that receives one.
 */
const downloadWorkbook = (result: TYPES.ReportExportResponse) => {
  const binary = window.atob(result.base64 ?? "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type:
      result.mimeType ||
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = result.fileName || "report.xlsx";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const ReportExportModal: React.FC<ReportExportModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  filter,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { result: options } = useSelector(getReportOptions);
  const { result: exportResult, loading } = useSelector(exportReport);

  const subjectFieldList = useMemo(
    () => options?.subjectFieldList ?? [],
    [options]
  );
  const questionList = useMemo(() => options?.questionList ?? [], [options]);

  const [selectedSubjectFieldKeyList, setSelectedSubjectFieldKeyList] =
    useState<string[]>([]);
  const [selectedQuestionIdList, setSelectedQuestionIdList] = useState<
    string[]
  >([]);

  // Defaults come from the backend catalogue (Name + Age ticked, every
  // question ticked) and are re-applied every time the popup opens, so a
  // half-finished selection never leaks into the next export.
  useEffect(() => {
    if (!isModalVisible) return;
    const defaultSubjectFieldKeyList = subjectFieldList
      .filter((x) => x.isDefault)
      .map((x) => x.key ?? "")
      .filter(Boolean);
    setSelectedSubjectFieldKeyList(
      defaultSubjectFieldKeyList.length
        ? defaultSubjectFieldKeyList
        : DEFAULT_REPORT_SUBJECT_FIELD_KEY_LIST
    );
    setSelectedQuestionIdList(
      questionList.map((x) => x.id ?? "").filter(Boolean)
    );
  }, [isModalVisible, subjectFieldList, questionList]);

  useEffect(() => {
    if (!exportResult) return;
    if (exportResult.error?.message) {
      toast.error(exportResult.error.message);
      dispatch(exportReportClear());
      return;
    }
    // Too large to hand back inside the response: the export worker is
    // building the full file and it will appear in the notification bell.
    // Nothing is truncated any more, so this is a success path, not a warning.
    if (exportResult.isQueued) {
      toast.success(
        t("report_export_queued") ||
          "This report is large, so it is being prepared in the background. " +
            "It will appear in your notifications when it is ready."
      );
      dispatch(exportReportClear());
      setIsModalVisible(false);
      return;
    }
    if (exportResult.base64) {
      downloadWorkbook(exportResult);
      toast.success(
        t("report_export_success") || "Report exported successfully"
      );
      dispatch(exportReportClear());
      setIsModalVisible(false);
    }
  }, [exportResult, dispatch, setIsModalVisible, t]);

  const isAllSubjectFieldsSelected =
    subjectFieldList.length > 0 &&
    selectedSubjectFieldKeyList.length === subjectFieldList.length;
  const isAllQuestionsSelected =
    questionList.length > 0 &&
    selectedQuestionIdList.length === questionList.length;

  const handleToggleAllSubjectFields = (checked: boolean) => {
    setSelectedSubjectFieldKeyList(
      checked
        ? subjectFieldList.map((x) => x.key ?? "").filter(Boolean)
        : []
    );
  };

  const handleToggleAllQuestions = (checked: boolean) => {
    setSelectedQuestionIdList(
      checked ? questionList.map((x) => x.id ?? "").filter(Boolean) : []
    );
  };

  const handleExport = () => {
    if (
      selectedSubjectFieldKeyList.length === 0 &&
      selectedQuestionIdList.length === 0
    ) {
      toast.error(
        t("report_export_empty_selection") ||
          "Select at least one subject detail or question"
      );
      return;
    }
    dispatch(
      exportReportRequest({
        filter,
        data: {
          subjectFieldKeyList: selectedSubjectFieldKeyList,
          questionIdList: selectedQuestionIdList,
        },
      })
    );
  };

  const handleCancel = () => {
    dispatch(exportReportClear());
    setIsModalVisible(false);
  };

  return (
    <Modal
      title={
        <span className="report-export-modal-title">
          {t("export_report") || "Export Report"}
        </span>
      }
      open={isModalVisible}
      onCancel={handleCancel}
      width={760}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("cancel") || "Cancel"}
        </Button>,
        <Button
          key="export"
          type="primary"
          loading={loading}
          onClick={handleExport}
        >
          {t("export") || "Export"}
        </Button>,
      ]}
    >
      <div className="report-export-section">
        <div className="report-export-section-header">
          <h4>{t("subject_details") || "Subject Details"}</h4>
          <Checkbox
            checked={isAllSubjectFieldsSelected}
            indeterminate={
              selectedSubjectFieldKeyList.length > 0 &&
              !isAllSubjectFieldsSelected
            }
            onChange={(e) => handleToggleAllSubjectFields(e.target.checked)}
          >
            {t("select_all") || "Select All"}
          </Checkbox>
        </div>
        {subjectFieldList.length > 0 ? (
          <Checkbox.Group
            className="report-export-checkbox-group"
            value={selectedSubjectFieldKeyList}
            onChange={(value) =>
              setSelectedSubjectFieldKeyList(value as string[])
            }
          >
            {subjectFieldList.map((field) => (
              <Checkbox
                key={field.key}
                value={field.key}
                className="report-export-checkbox"
              >
                {field.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        ) : (
          <Empty description={t("no_data") || "No data"} />
        )}
      </div>

      <Divider className="report-export-divider" />

      <div className="report-export-section">
        <div className="report-export-section-header">
          <h4>{t("questions") || "Questions"}</h4>
          <Checkbox
            checked={isAllQuestionsSelected}
            indeterminate={
              selectedQuestionIdList.length > 0 && !isAllQuestionsSelected
            }
            onChange={(e) => handleToggleAllQuestions(e.target.checked)}
          >
            {t("select_all") || "Select All"}
          </Checkbox>
        </div>
        {questionList.length > 0 ? (
          <Checkbox.Group
            className="report-export-checkbox-group"
            value={selectedQuestionIdList}
            onChange={(value) => setSelectedQuestionIdList(value as string[])}
          >
            {questionList.map((question) => {
              // The backend already sends the capped label; resolving again
              // here keeps the popup correct for a cached response written
              // before the Short Question was configured.
              const shortQuestion = resolveShortQuestion(
                question.shortQuestion,
                question.name,
                options?.shortQuestionMaxLength ?? SHORT_QUESTION_MAX_LENGTH
              );
              return (
                <Checkbox
                  key={question.id}
                  value={question.id}
                  className="report-export-checkbox"
                >
                  <Tooltip title={question.name}>
                    <span className="report-export-short-question">
                      {shortQuestion}
                    </span>
                  </Tooltip>
                </Checkbox>
              );
            })}
          </Checkbox.Group>
        ) : (
          <Empty
            description={
              t("no_questions_configured") ||
              "No questions configured for this client"
            }
          />
        )}
      </div>
    </Modal>
  );
};

export default ReportExportModal;
